import React, { useState, useEffect, useMemo } from 'react';
import { Button, Form, Input, message, Select } from 'antd';
import user from 'asset/img/login_user.png';
import psw from 'asset/img/login_psw.png';
import phone from 'asset/img/login_phone.png';
import bkLogin from 'asset/img/bk-login.webp';
import loginScroll from 'asset/img/login-scroll.png';
import registerTitle from 'asset/img/register-title.png';
import back from 'asset/img/back.png';
import { useNavigate, useLocation } from 'react-router-dom';
import { userRegister } from 'network';
import WechatBrowserTip from 'components/WechatBrowserTip';
import './index.scss';

// 约定：下拉框提交的是「序号」字符串（0/1/2...），后端再映射为文案入库
const AGE_OPTIONS = [3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((n, i) => ({ label: `${n}岁`, value: String(i) }));
const GENDER_OPTIONS = [
  { label: '男', value: '0' },
  { label: '女', value: '1' },
];
const ENGLISH_OPTIONS = [
  { label: '正在建立英语兴趣阶段', value: '0', desc: '（几乎没有系统学习过）' },
  { label: '已具备基础词汇认知', value: '1', desc: '（能听懂或认识部分常见单词）' },
  { label: '能理解简单表达结构', value: '2', desc: '（能听懂简单短语或句型）' },
];
const FORM_ITEM_FULL = 'register-form-item-full';

const isWechat = () => /micromessenger/i.test(navigator.userAgent);
const isAndroid = () => /android/i.test(navigator.userAgent);

const Register = () => {
  const [form] = Form.useForm();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const invite = searchParams.get('invite') || searchParams.get('invite_code') || searchParams.get('inviteCode') || searchParams.get('ref') || searchParams.get('code');
  const [showWechatTip, setShowWechatTip] = useState(false);
  const [pwdErrors, setPwdErrors] = useState({});
  const [count, setCount] = useState(null);
  const [disabled, setDisabled] = useState(false);
  let navigate = useNavigate();

  const hasInvite = !!invite;
  // 安卓 + 有邀请：注册成功后跳下载页；苹果不跳
  const shouldRedirectToDownload = useMemo(
    () => isAndroid() && hasInvite,
    [hasInvite]
  );

  useEffect(() => {
    if (isWechat()) {
      setShowWechatTip(true);
    }
  }, []);

  const handleBack = () => {
    navigate(`/login`);
  };

  const validatePasswords = (_, value) => {
    const pwd = form.getFieldValue('pwd');
    if (pwd && value && pwd !== value) {
      return Promise.reject(new Error('两次输入的密码不一致'));
    }
    return Promise.resolve();
  };

  const getCaptcha = () => {
    message.loading('正在获取验证码...', 0);
    setDisabled(true);
    const timer = setTimeout(() => {
      message.success('验证码已发送');
      setCount(60);
      const interval = setInterval(() => {
        setCount((prevCount) => prevCount - 1);
        if (count <= 0) {
          clearInterval(interval);
          setDisabled(false);
        }
      }, 1000);
      return () => clearInterval(interval);
    }, 2000);
    return () => clearTimeout(timer);
  };

  // 密码规则：至少 6 位，且包含「字母 + 数字」，不再要求必须有大写字母
  const passwordRegex = /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d]{6,}$/;
  const passwordErrorMessage = '密码需包含字母和数字，且长度至少为6位';

  const onFinish = (values) => {
    const inviteVal = invite || '1';
    const toStr = (v) => (v == null ? '' : typeof v === 'string' ? v.trim() : String(v));
    const payload = {
      mobile: values?.mobile,
      pwd: values?.pwd,
      name: values?.name,
      invite: inviteVal,
      age: toStr(values?.age),
      gender: toStr(values?.gender),
      english_level: toStr(values?.english_level),
      grade: values?.grade || '',
    };
    userRegister(payload).then((res) => {
      if (res?.code === 1) {
        return message.info('用户已存在');
      }
      message.info('注册成功');
      navigate(shouldRedirectToDownload ? '/download' : '/login');
    }).catch((err) => {
      message.error(err?.response?.data?.msg || err?.message || '注册失败，请重试');
    });
  };

  return (
    <div className='register-container'>
      <WechatBrowserTip visible={showWechatTip} onClose={() => setShowWechatTip(false)} />

      {/* 背景图片 */}
      <img src={bkLogin} alt="" className="register-bg-img" />
      
      {/* 飘动的云朵 */}
      <div className="register-clouds">
        <div className="cloud cloud-1"></div>
        <div className="cloud cloud-2"></div>
        <div className="cloud cloud-3"></div>
      </div>
      
      {/* 返回按钮 */}
      {!hasInvite && (
        <div className='register-back' onClick={handleBack}>
          <img src={back} alt="返回" />
        </div>
      )}
      
      {/* 卷轴登录框 */}
      <div className='register-card'>
        <img src={loginScroll} alt="" className='register-scroll-img' aria-hidden />
        <div className='register-card-inner'>
          <img src={registerTitle} alt="注册" className='register-title-img' />
          <Form
            form={form}
            name="register"
            onFinish={onFinish}
            className='register-form'
          >
          <Form.Item
            name="mobile"
            rules={[
              { required: true, message: '请输入手机号' },
              { type: 'string', pattern: /^1[3-9]\d{9}$/, message: '请输入有效的手机号' },
            ]}
          >
            <Input 
              prefix={<img src={phone} className='input-icon' />} 
              placeholder="请输入手机号" 
            />
          </Form.Item>
          
          <Form.Item
            name="name"
            rules={[
              { required: true, message: '请输入小朋友姓名' },
              {
                validator: (_, value) => {
                  const s = value != null ? String(value).trim() : '';
                  if (s.length < 2) return Promise.reject(new Error('姓名至少需要2个字符'));
                  if (s.length > 4) return Promise.reject(new Error('姓名最多4个字符'));
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input 
              prefix={<img src={user} className='input-icon' />} 
              placeholder="请输入小朋友姓名" 
              maxLength={4}
            />
          </Form.Item>

          <Form.Item
            name="age"
            className={FORM_ITEM_FULL}
            rules={[{ required: true, message: '请选择小朋友年龄' }]}
          >
            <Select
              placeholder="请选择小朋友年龄"
              options={AGE_OPTIONS}
              allowClear={false}
              className="register-select"
              popupClassName="register-select-dropdown"
            />
          </Form.Item>

          <Form.Item name="gender" className={FORM_ITEM_FULL} rules={[{ required: true, message: '请选择小朋友性别' }]}>
            <Select
              placeholder="请选择小朋友性别"
              options={GENDER_OPTIONS}
              allowClear={false}
              className="register-select"
              popupClassName="register-select-dropdown"
            />
          </Form.Item>

          <Form.Item
            name="english_level"
            className={FORM_ITEM_FULL}
            rules={[{ required: true, message: '请建立语言档案' }]}
          >
            <Select
              placeholder="建立语言档案"
              options={ENGLISH_OPTIONS}
              allowClear={false}
              className="register-select"
              popupClassName="register-select-dropdown"
              optionRender={(option) => (
                <div>
                  <div>{option.data.label}</div>
                  <div className="register-english-option-desc">{option.data.desc}</div>
                </div>
              )}
            />
          </Form.Item>
          
          <Form.Item
            name="pwd"
            rules={[
              { required: true, message: '请输入密码' },
              { pattern: passwordRegex, message: passwordErrorMessage },
            ]}
          >
            <Input.Password 
              prefix={<img src={psw} className='input-icon' />} 
              placeholder="请输入密码" 
            />
          </Form.Item>
          
          <Form.Item
            name="repwd"
            dependencies={['pwd']}
            rules={[{ validator: async (_, value) => { await validatePasswords(_, value); } }]}
          >
            <Input.Password 
              prefix={<img src={psw} className='input-icon' />} 
              placeholder="请再次输入密码" 
            />
          </Form.Item>
          
          <Form.Item className='register-btn-wrap'>
            <Button block type="primary" htmlType="submit" className='register-btn'>
              注 册
            </Button>
          </Form.Item>
        </Form>
          <div className='register-footer'>
            <span className='footer-text'>已有账号?</span>
            <span className='footer-link' onClick={handleBack}>立即登录</span>
          </div>
        </div>
      </div>
    </div>
  );
};
export default Register;
